<template>
  <section-vue class="business-reviews">
    <div class="business-reviews__container container">
      <h2 class="business-reviews__headline">
        {{ $t(`${this.i18nKey}.headline`) }}
      </h2>

      <div class="business-reviews__nav">
        <div class="business-reviews__nav-left" @click="prevClick">
          <sprite
            id="app/arrow"
            class="business-reviews__nav-left-icon"
            :class="{
              'business-reviews__nav-left-icon--active': leftNavIconActive
            }"
          />
        </div>

        <div class="business-reviews__current-review-copy">
          {{ currentReview.copy }}
        </div>

        <div class="business-reviews__nav-right" @click="nextClick">
          <sprite
            id="app/arrow"
            class="business-reviews__nav-right-icon"
            :class="{
              'business-reviews__nav-right-icon--active': rightNavIconActive
            }"
          />
        </div>
      </div>

      <div class="business-reviews__current-review-author" v-html="currentReview.author" />

      <div class="business-reviews__dot-nav">
        <button
          v-for="(review, index) in reviews"
          :key="index"
          @click="gotoReview(index)"
          class="business-reviews__nav-dot"
          :class="{
            'business-reviews__nav-dot--active': currentReviewIndex === index
          }"
        />
      </div>
    </div>
  </section-vue>
</template>

<script>
import Hammer from "hammerjs"
import SectionVue from "@/shared/section"
import Sprite from "@/shared/sprite"
import "/wds/sprites/app/arrow.svg"
import { trackEvent } from "@/services/tracking"

export default {
  name: "BusinessCompaniesLogos",
  components: {
    SectionVue,
    Sprite
  },
  props: {
    i18nKey: { type: String, default: "b2b" }
  },
  data() {
    return {
      currentReviewIndex: 0
    }
  },
  computed: {
    reviews() {
      return this.$t(`${this.i18nKey}.reviews`)
    },
    currentReview() {
      return this.reviews[this.currentReviewIndex]
    },
    leftNavIconActive() {
      return this.currentReviewIndex > 0
    },
    rightNavIconActive() {
      return this.currentReviewIndex < this.reviews.length - 1
    }
  },
  mounted() {
    const mc = new Hammer(this.$el)

    mc.on("swipeleft", () => {
      this.nextReview()
      trackEvent("body-20210217-business_reviews", "1-swipe", "next")
    })

    mc.on("swiperight", () => {
      this.prevReview()
      trackEvent("body-20210217-business_reviews", "1-swipe", "previous")
    })
  },
  methods: {
    prevClick() {
      this.prevReview()
      trackEvent("body-20210217-business_reviews", "1-click", "previous")
    },
    prevReview() {
      const newIndex = this.currentReviewIndex - 1

      if (newIndex < 0) {
        return
      }

      this.currentReviewIndex = newIndex
    },
    nextClick() {
      this.nextReview()
      trackEvent("body-20210217-business_reviews", "1-click", "next")
    },
    nextReview() {
      const newIndex = this.currentReviewIndex + 1

      if (newIndex >= this.reviews.length) {
        return
      }

      this.currentReviewIndex = newIndex
    },
    gotoReview(index) {
      this.currentReviewIndex = index
      trackEvent("body-20210217-business_reviews", "1-click", index)
    }
  },
  i18n: {
    messages: {
      en: {
        b2b: {
          headline: "What our partners say",
          reviews: [
            {
              copy: "I purchased Blinkist subscriptions for all my direct reports as a way to say “thank you” for a great 2020, but more importantly to set up learning conversations we could have as a leadership team. In our bi-weekly meetings, the team brings their favorite reads that have made a difference, and we chat about them. Blinkist gives us a great platform for sharing this content, and is helping me set up a powerful foundation for my leadership team to live out a growth mindset in 2021.",
              author: "Lloyd Wilky, Director Risk Operations Amazon Employee Services"
            },
            {
              copy: "Introducing audio into our learning offering was a given in 2021. We want people to learn in the flow of life right—when they're exercising, cooking or just lying face down on the sofa! Blinkist has given our employees access to a nonfiction wishlist they could have only dreamed of getting through before. And their feedback has been amazing—rating it 4.7 for content and 4.8 for UX out of 5! We’re so pleased to see these insights that would otherwise require years of reading to gain, are now easily accessible to everyone in 15-20 minute chunks!  Plus it's great that it’s not just corporate stuff. There’s loads on love and relationships, mental health, personal development, how humans work—all things that impact us on a daily basis. We’re missing a trick if we exclude this content from our offering and ignore the everyday challenges people face that affects how well they can do their job and how motivated they are each day.",
              author: "Amritha Murali, Global Digital Learning Innovations Lead BAT"
            },
            {
              copy: "Blinkist is a great way for the entire team to get up to speed quickly and learn about things like creativity, communication and other important work skills.",
              author: "Lilly Miner, Content Marketing Babbel"
            }
          ]
        },
        b2c: {
          headline: "Why your team will love Blinkist",
          reviews: [
            {
              copy: "If you’re in a leadership role, acquiring knowledge is critical. That’s why Blinkist is important to me—it allows me to easily stay on top of things, and gives me extra confidence that I’m providing good advice and mentorship. It's my pocket resource for instant answers on topics such as business, leadership, relationships, science, politics, etc. In less than 12 minutes I get what I need from experts. Blinkist empowers me for very little cost and time. It’s efficient and engaging. And anyone who wants to succeed in life should have it.",
              author:
                "<a href='https://www.linkedin.com/in/barry-andruschak-095b7851/?originalSubdomain=ca'>Barry Andruschak</a>, National Sales Director Primerica"
            },
            {
              copy: "The content of Blinkist is amazing. When I wake up in the morning, the first thing I do is listen to a 15 minute book summary and get useful information on my topic of interest. Recommended to everyone who has a busy life but wants to take breaks and listen to great writers giving life advice.",
              author: "<a href='https://www.linkedin.com/in/saidakbarp/'>Said Pardaev</a>, Data Scientist Uber"
            },
            {
              copy: "I use Blinkist when I’m in between meetings and can’t get a giant task done, but at least I can be productive.",
              author:
                "Sarah Schupp, Professor & Founder of <a href='https://www.universityparent.com/'>UniversityParent.com</a>"
            },
            {
              copy: "A vast majority of non-fiction publications contain maybe 3 or 4 great nuggets of wisdom at most. The rest of the pages are just fillers, such as stories and fluff, to engage the reader. Blinkist does the heavy lifting and presents fun to read and a fantastic summary that I can finish in 15 minutes. I don’t need speed reading, and I don’t need to worry about missing important stuff. Blinkist makes sure that all the important wisdom is accounted for.",
              author: "Jon Bear,<a href='https://www.youtube.com/jonbear'> Videographer and Blogger"
            },
            {
              copy: "There’s so much information in the world today, and it seems as if there is less time than ever before. Blinkist’s team of experts sifts through books for me to find the signal and avoid the noise.",
              author:
                "Aidan McCullen, Author of <a href='https://theinnovationshow.io/undisruptable-book/'>“Undisruptable”</a> & Host of <a href='https://theinnovationshow.io/'> The Innovation Show</a>"
            }
          ]
        }
      },
      de: {
        b2b: {
          headline: "Das sagen unsere Business Kunden",
          reviews: [
            {
              copy: "Ich habe meinen Mitarbeitern die Blinkist Business Abos geschenkt, sowohl um mich für ein erfolgreiches Jahr 2020 zu bedanken, als auch um Gespräche über Bildung im Leadership-Team voranzutreiben. Alle zwei Wochen stellt das Team nun die Blinks vor, die sie am meisten bewegt haben, um diese persönlich zu diskutieren. Blinkist bietet uns eine tolle Möglichkeit, dieses Wissen miteinander zu teilen und eine starke Basis, um die Wachstums Mentalität im Team zu fördern.",
              author: "Lloyd Wilky, Director Risk Operations Amazon Employee Services"
            },
            {
              copy: "Wir haben im Jahr 2021 Audio als Teil unserer Weiterbildung eingeführt, da wir Wissen leichter dorthin bringen wollten, wo das Leben stattfindet – beim Sport Machen, beim Kochen oder einfach auf dem Sofa. Blinkist verschafft unseren Mitarbeitern Zugang zu einer riesigen Auswahl an Sachbüchern, von der sie vorher nur träumen konnten. Das Feedback ist überwältigend positiv: Die Blinkist-Inhalte wurden mit 4,7 und die Anwendbarkeit mit 4,8 von 5 Punkten bewertet. Wir freuen uns darüber, Eindrücke zu gewinnen und Ideen zu entwickeln, die sonst jahrelanges Lesen erfordern würden und nun innerhalb von 15 bis 20 Minuten erfasst werden können. Das Angebot umfasst viele Titel zu Themen wie Liebe und Beziehungen, psychischer Gesundheit, persönlicher Entwicklung und dazu, wie Menschen funktionieren – Wissen, das uns täglich beeinflusst.",
              author: "Amritha Murali, Global Digital Learning Innovations Lead BAT"
            },
            {
              copy: "Blinkist eignet sich hervorragend, um ein Thema in kurzer Zeit zu verstehen und Bereiche wie Kreativität, Kommunikation und andere Karriere relevante Soft Skills zu erlernen.",
              author: "Lilly Miner, Content Marketing Babbel"
            }
          ]
        },
        b2c: {
          headline: "Das sagen unsere Kunden",
          reviews: [
            {
              copy: "Wenn Sie in einer Führungsrolle sind, ist Bildung unentbehrlich. Deshalb ist Blinkist so wichtig für mich: Ich bleibe auf dem Laufenden und kann meinen Mitarbeitern mit gutem Gewissen fundiertes Mentoring bieten. Blinkist ist meine Quelle für sofortige Antworten zu Themen wie Arbeit, Führung, Beziehungen, Wissenschaft, Politik, usw. In weniger als 15 Minuten bekomme ich, was ich brauche, und zwar von Experten. Blinkist ist nicht nur effizient, sondern auch unterhaltsam. Jeder, der im Leben erfolgreich sein will, sollte Blinkist nutzen.",
              author:
                "<a href='https://www.linkedin.com/in/barry-andruschak-095b7851/?originalSubdomain=ca'>Barry Andruschak</a>, National Sales Director Primerica"
            },
            {
              copy: "Blinkist ist großartig. Das Erste, was ich morgens nach dem Aufwachen tue, ist, mir eine 15-minütige Buchzusammenfassung anzuhören und nützliche Informationen über Themen zu erhalten, die mich interessieren. Empfehlenswert für alle, die in ihrem schnelllebigen Alltag Pausen machen wollen, um sich mit Expertenwissen weiterzubilden.",
              author: "<a href='https://www.linkedin.com/in/saidakbarp/'>Said Pardaev</a>, Data Scientist bei Uber"
            },
            {
              copy: "Ich benutze Blinkist am liebsten, wenn ich zwischen zwei Meetings stecke und produktiv sein will.",
              author: "Sarah Schupp, Professorin und Gründerin der <a href='https://www.universityparent.com/'></a>"
            },
            {
              copy: "Die Mehrheit der veröffentlichen Sachbücher enthält vielleicht 3 oder 4 Kernaussagen, wenn überhaupt. Der Rest ist nur Füllmaterial, um den Leser bei der Stange zu halten. Blinkist bietet sowohl unterhaltsame Lektüre als auch fantastische Zusammenfassungen, die ich in 15 Minuten lesen kann – ohne die Sorge, wichtiges zu verpassen.",
              author: "Jon Bear,<a href='https://www.youtube.com/jonbear'> Videograph und Blogger"
            },
            {
              copy: "Es gibt heutzutage so viele Informationen in der Welt, und gleichzeitig steht uns weniger Zeit zur Verfügung als früher. Das Expertenteam von Blinkist durchforstet für mich die Bücher, findet das Wesentliche und erspart mir Nebensächlichkeiten.",
              author:
                "Aidan McCullen, Autor von <a href='https://theinnovationshow.io/undisruptable-book/'>“Undisruptable”</a> und Gastgeber von <a href='https://theinnovationshow.io/'> The Innovation Show</a>"
            }
          ]
        }
      }
    }
  }
}
</script>

<style lang="scss">
.business-reviews {
  $root: &;

  text-align: center;

  background-color: $background-blue;

  &__headline {
    @include font-style(h3);

    margin-bottom: $spacing-32;
  }

  &__current-review-copy {
    @include font-style(p);
  }

  &__current-review-author {
    @include font-style(h5, $color: $mid-grey);

    margin-bottom: $spacing-24;
  }

  &__nav {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: $spacing-24;
  }

  &__nav-left {
    padding-right: $spacing-16;
  }

  &__nav-left-icon {
    flex-shrink: 0;
    width: auto;

    color: $light-grey;

    transform: rotate(90deg);

    &--active {
      color: $blue;

      cursor: pointer;
    }
  }

  &__nav-right {
    padding-left: $spacing-16;
  }

  &__nav-right-icon {
    flex-shrink: 0;
    width: auto;

    color: $light-grey;

    transform: rotate(-90deg);

    &--active {
      color: $blue;

      cursor: pointer;
    }
  }

  &__dot-nav {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__nav-dot {
    width: $spacing-8;
    height: $spacing-8;

    background-color: $light-grey;

    border-radius: 50%;

    cursor: pointer;

    &:not(:last-child) {
      margin-right: $spacing-24;
    }

    &--active {
      background-color: $blue;

      cursor: auto;
    }
  }

  @include media(min-width, m) {
    &__headline {
      @include font-style(h2);

      margin-bottom: $spacing-48;
    }

    &__current-review-copy {
      @include font-style(p0);
    }

    &__nav-left {
      padding-right: $spacing-32;
    }

    &__nav-right {
      padding-right: $spacing-32;
    }
  }
}
</style>
